// AddEventFooter — Presentational footer/submit bar for the AddEventForm: the
// top-bordered row holding the "Cancel" button and the primary submit button.
// The submit button reads "Add {label}" with a Save icon, and switches to a
// spinner + "Adding..." label with a disabled, gray, not-allowed state while a
// save is in flight.
//
// Extracted verbatim from `AddEventForm.js` (the "Action Buttons" block) as part
// of the behavior-preserving refactor (issue #240). In the source the Cancel
// button called the `onCancel` prop and the submit button called `handleSave`,
// with the `isSaving` flag driving the disabled state, the background color, the
// cursor, and the spinner/label swap; those arrive here as the `isSaving`,
// `onCancel`, and `onSave` props so the component stays render-only with NO
// internal state. The submit label uses the shared `getEventTypeLabel` pure
// logic (task 13.2), preserving the generic "Add Event" label for
// unknown/missing types. Button labels, the inline spinner markup + `spin`
// animation, the inline hover handlers, the Save icon (lucide-react), and styles
// are identical to the source.
//
// Props:
//   eventType - string; selects the submit label via getEventTypeLabel
//   isSaving  - boolean; drives the disabled state, colors, cursor, and the
//               spinner/"Adding..." vs "Add {label}" swap
//   onCancel  - () => void invoked by the "Cancel" button
//   onSave    - () => void invoked by the submit button
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import { Save } from 'lucide-react';
import { getEventTypeLabel } from '../logic/getEventTypeLabel';

export const AddEventFooter = React.memo(function AddEventFooter({
  eventType,
  isSaving,
  onCancel,
  onSave
}) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px',
      paddingTop: '16px',
      borderTop: '1px solid #e5e7eb'
    }}>
      <button
        onClick={onCancel}
        style={{
          padding: '10px 20px',
          border: '1px solid #d1d5db',
          backgroundColor: 'white',
          color: '#374151',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
      >
        Cancel
      </button>
      <button
        onClick={onSave}
        disabled={isSaving}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 20px',
          backgroundColor: isSaving ? '#9ca3af' : '#059669',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: isSaving ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          if (!isSaving) e.target.style.backgroundColor = '#047857';
        }}
        onMouseLeave={(e) => {
          if (!isSaving) e.target.style.backgroundColor = '#059669';
        }}
      >
        {isSaving ? (
          <>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid white',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            Adding...
          </>
        ) : (
          <>
            <Save size={16} />
            Add {getEventTypeLabel(eventType)}
          </>
        )}
      </button>
    </div>
  );
});

export default AddEventFooter;
